import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Checkbox, DatePicker, Select, Tabs, Typography } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { useLiveTopic } from '../../../live/LiveContext.js';
import { downloadExport } from '../../../domain/tableExport.js';
import { useStepUp } from '../../../stepup/useStepUp.js';
import RunDrawer from '../unitPrices/RunDrawer.jsx';
import { PricesTable, RunsTable } from '../unitPrices/UnitPrices.jsx';
import { latestFileRuns, pricesExportQuery, pricesQuery, runsQuery } from '../unitPrices/runs.js';
import FundSummaryCards from './FundSummaryCards.jsx';
import FundUnitAllocation from './FundUnitAllocation.jsx';
import LegacyHistory from './LegacyHistory.jsx';
import TransactionHistory from './TransactionHistory.jsx';
import { usePagedList } from './usePagedList.js';
import { colomboToday, extractQuery, fundName, fundOf, hashFor, resolveRoute, routeFromHash } from './register.js';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const day = (range, i) => range?.[i]?.format('YYYY-MM-DD') ?? null;

/** A panel's hint, in the gold info banner the Console's refreshed panels share. */
const InfoBanner = ({ children }) => (
    <div className="unit-prices-info">
        <ActionIcon name="info" />
        <span>{children}</span>
    </div>
);

/** The route the page opened on; no window (a server render): every fund, the first view. */
const initialRoute = () => routeFromHash(typeof window === 'undefined' ? '' : window.location.hash);

/**
 * The Fund Register (IP-51, IP-53, IP-109, IP-112; design 2026-10-02 section
 * 8), on the Console API - the Core's register, read through GET register/...
 * (register.js says which read feeds what). It replaces the prototype's
 * in-browser book, which nothing filled.
 *
 * Opens on the fund cards. A card drills into its fund: Holders (paged, with
 * value, share, cost and young and old units, and a holder's drawer),
 * Transactions (filters and Load more) and History (the INITIAL history,
 * apart from the register's own transactions). Transactions and History also
 * read every fund. The route is #transactions/registry[/<fund>][/<view>]: a
 * fund segment is taken when it looks like a fund code and the API lists it
 * (register.js routeFromHash, resolveRoute); otherwise the page says so and
 * shows every fund.
 *
 * The Unit Prices screen lives here too, for whoever holds core.prices.view.
 * NAV runs (the latest uploaded file, every fund in it, with Approve and
 * Reject) sit under the cards and are not narrowed by the selected card.
 * Price History sits at the bottom. Its fund dropdown starts on every fund
 * and follows the selected card; choosing a fund in the dropdown filters
 * only that list.
 *
 * Whoever holds core.registry.extract may download the Rule 37(3) extract -
 * the selected fund or every fund, as at a date - as an Excel file the
 * server stores and links (register/extract?format=xlsx; domain/tableExport.js).
 * The Transactions and Price history Download buttons work the same way.
 */
const UnitRegistry = ({ onRunsChanged }) => {
    const { api, canAct, canView } = useAccess();
    const { message } = App.useApp();
    const canExtract = canAct('registry', 'extract');
    const canPrices = canView('unitPrices');
    const canRegistry = canView('registry');
    const { withStepUp, stepUpDialog } = useStepUp();

    const [funds, setFunds] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [error, setError] = useState(null);
    const [requested, setRequested] = useState(initialRoute);
    const [holder, setHolder] = useState(null);
    const [asAt, setAsAt] = useState(() => dayjs(colomboToday()));
    const [extracting, setExtracting] = useState(false);
    const [downloadingPrices, setDownloadingPrices] = useState(false);
    // Bumped by Refresh, so the open view reads its list again too.
    const [generation, setGeneration] = useState(0);
    // The run whose drawer is open, and the decision a row's button started there (or null).
    const [openRun, setOpenRun] = useState(null);
    // The file the runs shown came from (GET pricing/runs?batch=latest names it), or null before it is read / when none.
    const [runFile, setRunFile] = useState(null);
    // Price History's own fund. A card sets it; the dropdown does not set the card.
    const [priceFund, setPriceFund] = useState(() => initialRoute().fundId);
    const [priceRange, setPriceRange] = useState(null);
    const [superseded, setSuperseded] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const answer = await api.register.funds();
            setFunds(answer.funds ?? []);
            setRefreshedAt(new Date());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        load();
    }, [load]);

    const { fundId, view, unknownFund } = resolveRoute(requested, funds);
    const fund = fundOf(funds, fundId);

    // The card (and All funds, and the Transactions / History fund dropdown) moves this list.
    // Changing the dropdown itself does not: it filters Price History alone.
    useEffect(() => {
        setPriceFund(fundId);
    }, [fundId]);

    // A fund the API does not list leaves the hash: the page shows every fund and says why.
    useEffect(() => {
        if (unknownFund && typeof window !== 'undefined') window.location.hash = hashFor(null, view);
    }, [unknownFund, view]);

    const go = (nextFundId, nextView) => {
        setRequested({ fundId: nextFundId, view: nextView });
        // A holder belongs to a fund's view: picking another fund clears them.
        if (nextFundId !== fundId) setHolder(null);
        window.location.hash = hashFor(nextFundId, nextView);
    };

    const fetchRuns = useCallback(async (cursor) => {
        const page = await api.pricing.runs(runsQuery({ batch: 'latest', cursor }));
        if (page.batch !== undefined) {
            if (!cursor) setRunFile(page.batch);
            return { items: page.runs ?? [], nextCursor: page.nextCursor ?? null };
        }
        // An API from before ?batch= ignores it and lists every run: keep the newest run's file here instead (runs.js latestFileRuns).
        const runsOfFile = latestFileRuns(page.runs);
        let fileName = null;
        if (runsOfFile.length) {
            try {
                fileName = (await api.pricing.run(runsOfFile[0].navRunId)).batch?.fileName ?? null;
            } catch {
                fileName = null;
            }
        }
        setRunFile(runsOfFile.length ? { batchId: runsOfFile[0].batchId, fileName, businessDate: runsOfFile[0].valuationDate } : null);
        return { items: runsOfFile, nextCursor: null };
    }, [api]);
    const runs = usePagedList(canPrices ? fetchRuns : null, 'navRunId');

    const priceFrom = day(priceRange, 0);
    const priceTo = day(priceRange, 1);
    const fetchPrices = useCallback(async (cursor) => {
        const page = await api.pricing.prices(pricesQuery({ fundId: priceFund, from: priceFrom, to: priceTo, includeSuperseded: superseded, cursor }));
        return { items: page.prices ?? [], nextCursor: page.nextCursor ?? null };
    }, [api, priceFund, priceFrom, priceTo, superseded]);
    const prices = usePagedList(canPrices ? fetchPrices : null, 'priceId');

    const refresh = () => {
        load();
        setGeneration((n) => n + 1);
        runs.reload();
        prices.reload();
    };
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // holdings change - an order approved (its units posted), a holdings or transactions file accepted,
    // a holder approved or its status changed - or a price file is checked, or a run is decided.
    useLiveTopic(['register', 'holders', 'prices'], refresh);

    const decided = () => {
        refresh();
        onRunsChanged?.();
    };

    // The Rule 37(3) extract as Excel: the server stores the file and answers its link (register/extract?format=xlsx).
    const downloadExtract = async () => {
        setExtracting(true);
        try {
            const asked = { fundId, asAt: asAt.format('YYYY-MM-DD') };
            await downloadExport({
                call: () => api.register.extract(extractQuery({ ...asked, format: 'xlsx' })),
                fallbackName: 'register_extract_' + (fundId ?? 'ALL') + '_' + asked.asAt.replace(/-/g, '') + '.xlsx',
                message,
                what: 'The extract',
            });
        } finally {
            setExtracting(false);
        }
    };

    // Every price the history's filters match, not the page shown (pricing/prices/export, Excel).
    const downloadPrices = async () => {
        setDownloadingPrices(true);
        try {
            await downloadExport({
                call: () => api.pricing.pricesExport(pricesExportQuery({ fundId: priceFund, from: priceFrom, to: priceTo, includeSuperseded: superseded })),
                fallbackName: 'unit-prices_' + (priceFund ?? 'ALL') + '.xlsx',
                message,
                what: 'The price history',
            });
        } finally {
            setDownloadingPrices(false);
        }
    };

    const listed = funds ?? [];
    const fundOptions = listed.map((f) => ({ value: f.fundId, label: f.fundId + ' · ' + (f.name ?? f.fundId) }));
    const fundNames = Object.fromEntries(listed.map((f) => [f.fundId, f.name ?? f.shortName ?? null]));
    const shared = { funds: listed, fundId, onFund: (next) => go(next, view), holder, onHolder: setHolder };
    const items = [
        {
            key: 'allocation',
            label: 'Holders',
            children: <FundUnitAllocation key={generation} fund={fund} funds={listed} onHolderTransactions={(h) => { setHolder(h); go(fundId, 'transactions'); }} />
        },
        { key: 'transactions', label: 'Transactions', children: <TransactionHistory key={generation} {...shared} /> },
        { key: 'history', label: 'History', children: <LegacyHistory key={generation} {...shared} /> }
    ];

    return (
        <>
            <section className="panel register-panel">
                <div className="register-heading">
                    <h3 className="panel-title">Funds on the register</h3>
                    <div className="register-toolbar">
                        {fund && <Button className="register-toolbar-btn" icon={<ArrowLeftOutlined />} onClick={() => go(null, view)}>All funds</Button>}
                        <Button className="register-toolbar-btn refresh-button" icon={<ActionIcon name="refresh" />} onClick={refresh}
                            loading={loading || runs.loading || prices.loading}>Refresh</Button>
                        {canExtract && (
                            <>
                                <DatePicker className="register-toolbar-date" aria-label="Extract as at" prefix="Effective Date" value={asAt} format="YYYY-MM-DD"
                                    allowClear={false} disabledDate={(d) => d.format('YYYY-MM-DD') > colomboToday()} onChange={(d) => d && setAsAt(d)} />
                                <Button className="register-toolbar-btn" icon={<DownloadOutlined />} onClick={downloadExtract} loading={extracting}>
                                    Download Extract
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                <div className="register-info">
                    <ActionIcon name="info" />
                    <div>
                        <div>
                            {fund
                                ? 'Showing ' + fundName(fund) + '. Pick another card to switch funds, or go back to every fund.'
                                : canRegistry
                                    ? 'Select a fund to view its holders. The Transactions and History tabs display all funds until a specific fund is selected.'
                                    : 'Select a fund to filter price history.'}
                            {' '}Last refreshed {refreshedAt ? refreshedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Colombo' }) : '—'}.
                        </div>
                        {canExtract && (
                            <div>
                                The extract (Rule 37(3)) is {fund ? fund.fundId + '\'s' : 'every fund\'s'} register as at the date chosen, as an Excel file.
                            </div>
                        )}
                    </div>
                </div>
                {error && <Alert type="error" showIcon message={error} className="auth-alert" closable onClose={() => setError(null)} />}
                {unknownFund && (
                    <Alert type="info" showIcon className="auth-alert" message={unknownFund + ' is not a fund on the register; showing every fund.'} />
                )}
                <div className="register-cards">
                    <FundSummaryCards funds={funds ?? []} selectedFundId={fundId} onSelect={(next) => go(next, view)} />
                    {funds !== null && funds.length === 0 && <Text type="secondary">No fund is on the register yet.</Text>}
                </div>
            </section>

            {canPrices && (
                <section className="panel unit-prices-panel">
                    <div className="unit-prices-heading">
                        <h3 className="panel-title">{runFile?.fileName ?? 'NAV runs'}</h3>
                        <InfoBanner>
                            {runFile ? 'The NAV runs from the last file uploaded' + (runFile.businessDate ? ', for ' + runFile.businessDate : '') + '. ' : ''}
                            Select a fund to see its checks, the file it came from and its trail, and and to approve or reject it.
                        </InfoBanner>
                    </div>
                    {runs.error && <Alert type="error" showIcon message={runs.error} className="auth-alert" closable onClose={runs.clearError} />}
                    <RunsTable runs={runs.items} fundNames={fundNames} loading={runs.loading} onOpen={(id) => setOpenRun({ navRunId: id, intent: null })}
                        onDecide={(id, intent) => setOpenRun({ navRunId: id, intent })} />
                    {runs.nextCursor && <div className="register-list-footer"><Button onClick={runs.loadMore} loading={runs.loadingMore}>Load more</Button></div>}
                </section>
            )}

            {canRegistry && (
                <div className="panel">
                    <Tabs activeKey={view} onChange={(key) => go(fundId, key)} items={items} />
                </div>
            )}

            {canPrices && (
                <section className="panel unit-prices-panel">
                    <div className="price-history-toolbar">
                        <h3 className="panel-title">Price History</h3>
                        <div className="unit-prices-filters">
                            <Checkbox checked={superseded} onChange={(e) => setSuperseded(e.target.checked)}>Show superseded versions</Checkbox>
                            <Select aria-label="Fund" allowClear placeholder="Every Fund" className="unit-prices-fund-filter" value={priceFund ?? undefined}
                                onChange={(v) => setPriceFund(v ?? null)} options={fundOptions} />
                            <RangePicker aria-label="Effective dates" className="price-history-date-filter" value={priceRange} format="YYYY-MM-DD"
                                allowEmpty={[true, true]} onChange={(r) => setPriceRange(r ?? null)} />
                            <Button aria-label="Download price history" className="unit-prices-download" icon={<DownloadOutlined />}
                                onClick={downloadPrices} loading={downloadingPrices} disabled={downloadingPrices}>Download</Button>
                        </div>
                    </div>
                    <InfoBanner>Every published price, newest first. Imported history came from an initial load and is never used to price an order.</InfoBanner>
                    {prices.error && <Alert type="error" showIcon message={prices.error} className="auth-alert" closable onClose={prices.clearError} />}
                    <PricesTable prices={prices.items} loading={prices.loading} />
                    {prices.nextCursor && <div className="register-list-footer"><Button onClick={prices.loadMore} loading={prices.loadingMore}>Load more</Button></div>}
                </section>
            )}

            {canPrices && (
                <RunDrawer navRunId={openRun?.navRunId ?? null} intent={openRun?.intent ?? null} onClose={() => setOpenRun(null)}
                    withStepUp={withStepUp} onDecided={decided} />
            )}
            {canPrices && stepUpDialog}
        </>
    );
};

export default UnitRegistry;
